import { QueryKey } from "@tanstack/react-query";
import { QueryConfig } from "./types";
import { sanitizeKey } from "./utils";

/**
 * Creates a reusable query definition for TanStack Query.
 *
 * This function allows you to define a query with a key and fetch function,
 * which can be called later with optional variables. It supports both static
 * and dynamic query keys based on the provided variables.
 *
 * @template TVariables - The type of variables passed to the query (defaults to void)
 * @template TData - The type of data returned by the query
 *
 * @param config - Configuration object for the query
 * @param config.queryKey - Static query key or a function that generates a key from variables
 * @param config.queryFn - Async function that fetches the data using the provided variables
 *
 * @returns A function that accepts optional variables and returns a QueryConfig object
 *
 * @example
 * const getUserQuery = defineQuery({
 *   queryKey: (vars) => ['user', vars.id],
 *   queryFn: (vars) => fetchUser(vars.id)
 * });
 */
export function defineQuery<TVariables = void, TData = unknown>(config: {
  queryKey: QueryKey | ((variables: TVariables) => QueryKey);
  queryFn: (variables: TVariables) => Promise<TData>;
}) {
  return (options?: { variables?: TVariables }) => {
    const queryKey =
      typeof config.queryKey === "function"
        ? config.queryKey(options?.variables as TVariables)
        : config.queryKey;

    return {
      type: "query" as const,
      queryKey: sanitizeKey(queryKey),
      queryFn: () => config.queryFn(options?.variables as TVariables),
    } satisfies QueryConfig<TData>;
  };
}
